import { useLayoutEffect, useRef, useState } from "react";
import { nanoid } from "nanoid";
import { el as gr } from "date-fns/locale";
import {
  DateRangePicker,
  SearchSelect,
  SearchSelectItem,
  Select,
  SelectItem,
} from "@tremor/react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { formatKeepOnlyDate } from "../../utils/checkMisthosi";
import { uploadSocialImage } from "../../utils/socialImages";
import { eidi, eidiMap, eidiToEngMap } from "../../components/Social/Ekloges";
import {
  ImageAttachButton,
  ImageAttachPreview,
  imageFromClipboard,
} from "../../components/Social/ImageAttach";

const JSON_OPTIONS = {
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
};

const EMPTY_RANGE = { from: null, to: null };

// The post composer of the home page, with the same two-step submit as
// Ekloges.jsx (create the post, then attach its details). An είδος/τόπος
// turns the message into an αγγελία with an optional date range.
const PostComposer = ({ onPosted }) => {
  const axiosPrivate = useAxiosPrivate();
  const [body, setBody] = useState("");
  const [uuid, setUuid] = useState(nanoid);
  const [eidos, setEidos] = useState("");
  const [periohi, setPeriohi] = useState("");
  const [dateRange, setDateRange] = useState(EMPTY_RANGE);
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const textareaRef = useRef(null);

  // Grow with the text, up to the CSS max-height.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [body]);

  const canSubmit = !submitting && (body.trim() !== "" || Boolean(imageFile));

  const selectImage = (file) => {
    setImageFile(file);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");

    let image;
    if (imageFile) {
      try {
        image = await uploadSocialImage(axiosPrivate, imageFile);
      } catch (err) {
        console.error(err);
        setError("Η φωτογραφία δεν μπόρεσε να ανέβει. Δοκιμάστε ξανά ή επιλέξτε άλλη.");
        setSubmitting(false);
        return;
      }
    }

    try {
      await axiosPrivate.post("/posts", JSON.stringify({ body, uuid, image }), JSON_OPTIONS);
      await axiosPrivate.post(
        `/posts/${uuid}`,
        JSON.stringify({
          body: body.replaceAll("\n", " \n "),
          eidos: eidiToEngMap[eidos],
          periohi,
          dateRange: {
            from: formatKeepOnlyDate(dateRange.from),
            to: formatKeepOnlyDate(dateRange.to),
          },
        }),
        JSON_OPTIONS
      );
      setBody("");
      setEidos("");
      setPeriohi("");
      setDateRange(EMPTY_RANGE);
      setImageFile(null);
      setShowOptions(false);
      setUuid(nanoid());
      onPosted?.();
    } catch (err) {
      console.error(err);
      setError("Η ανάρτηση δεν ήταν δυνατή. Δοκιμάστε ξανά.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="ds-card ds-composer" onSubmit={handleSubmit}>
      <div className="ds-composer-input">
        <textarea
          ref={textareaRef}
          rows={2}
          value={body}
          placeholder="Γράψτε το μήνυμά σας εδώ..."
          aria-label="Νέα ανάρτηση"
          onChange={(e) => setBody(e.target.value)}
          onPaste={(e) => {
            const file = !submitting && imageFromClipboard(e);
            if (file) {
              e.preventDefault();
              selectImage(file);
            }
          }}
        />
      </div>
      {imageFile && (
        <div className="ds-composer-photo">
          <ImageAttachPreview
            file={imageFile}
            onRemove={() => setImageFile(null)}
            disabled={submitting}
          />
        </div>
      )}
      {error && (
        <p className="ds-form-error" role="alert">
          {error}
        </p>
      )}
      {showOptions && (
        <div className="ds-composer-options">
          <Select placeholder="Είδος" onValueChange={setEidos} value={eidos}>
            {eidi.map((dim) => (
              <SelectItem key={dim} value={dim}>
                {dim}
              </SelectItem>
            ))}
          </Select>
          {eidos && (
            <SearchSelect
              placeholder={eidos === "ΜΕΤΑΦΡΑΣΕΙΣ" ? "Γλώσσα" : "Τόπος"}
              onValueChange={setPeriohi}
              value={periohi}
            >
              {eidiMap[eidos]?.map((dim, i) => (
                <SearchSelectItem key={`${i}-${dim}`} value={dim}>
                  {dim}
                </SearchSelectItem>
              ))}
            </SearchSelect>
          )}
          {periohi && (
            <DateRangePicker
              value={dateRange}
              onValueChange={setDateRange}
              locale={gr}
              enableSelect={false}
              placeholder="Επιλέξτε Χρον.Διάστημα"
            />
          )}
        </div>
      )}
      <div className="ds-composer-actions">
        <ImageAttachButton onSelect={selectImage} disabled={submitting} />
        <button
          type="button"
          className={`ds-link-btn ${showOptions ? "is-active" : ""}`}
          onClick={() => setShowOptions((v) => !v)}
          aria-expanded={showOptions}
        >
          Αγγελία (είδος / τόπος)
        </button>
        <div className="ds-toolbar-spacer" />
        <button type="submit" className="ds-btn ds-btn--primary" disabled={!canSubmit}>
          {submitting ? "Αποστολή…" : "Αποστολή"}
        </button>
      </div>
    </form>
  );
};

export default PostComposer;
